import React from 'react';
import classnames from 'classnames';
import { FetchResult } from '@apollo/client';
import moment from 'moment';

import AnonEmployerAssociation from '@buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AnonymousUserDisplay from '@buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import ArticlePreview from '@buyside/components/presentation/Feed/ArticlePreview/articlePreview';
import { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import CommentBodyRenderer from '@buyside/components/presentation/CommentBodyRenderer/commentBodyRenderer';
import FollowButton, {
  FollowableType
} from '@buyside/components/presentation/FollowButton/followButton';
import Like from '@buyside/components/presentation/Feed/Actions/like';
import Link from '@buyside/components/presentation/Link/link';
import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import MessageButton from '@buyside/components/presentation/MessageModal/messageButton';
import ProfileImage, {
  IMAGE_SIZES
} from '@buyside/components/presentation/ProfileImage/profileImage';
import SocialMediaShareButtons from '@buyside/components/presentation/SocialMediaShareButtons/socialMediaShareButtons';
import YouTubePlayer from '@buyside/components/presentation/YouTubePlayer/youtubePlayer';

import {
  LikeEventMutation,
  Post as PostType,
  useLikeEventMutation,
  usePostShowQuery
} from '@interfaces/graphql-typings';

import {
  defaultUserImageUrl,
  employerProfileUrl,
  postsSearchUrl,
  postUrl,
  userUrl
} from '@buyside/util/urlHelper';

import EnvelopeIcon from '@buyside/svgs/envelope.svg';

import styles from './postShowStyles.scss';

const Post: React.FC<{ postId: string }> = ({ postId }): React.ReactElement => {
  const { data, loading, error } = usePostShowQuery({ variables: { postId } });

  const [likePost] = useLikeEventMutation({
    update(cache, { data }) {
      const hasLiked = Boolean(data?.likeEvent?.event?.currentUserHasLiked);

      cache.modify<PostType>({
        id: cache.identify({ id: postId, __typename: 'Post' }),
        fields: {
          currentUserHasLiked: () => hasLiked,
          likeCount: cachedLikeCount => (hasLiked ? cachedLikeCount + 1 : cachedLikeCount - 1)
        }
      });
    }
  });

  if (loading || !data) return <LoadingSpinner size={SIZES.LARGE} />;

  if (error || !data.post || !data.currentUser) throw error;

  const {
    id,
    body,
    createdAt,
    currentUserHasLiked,
    domain,
    eventId,
    likeCount,
    ogUrl,
    ogTitle,
    ogDescription,
    ogPreviewImageUrl,
    tagList,
    youtubeId,
    untaggedBody,
    user
  } = data.post;
  const { id: currentUserId, canViewUserDetails } = data.currentUser;

  return (
    <>
      <div className={styles.authorWrap}>
        <div>
          <ProfileImage
            source={canViewUserDetails ? user?.profileImageUrl : defaultUserImageUrl()}
            size={IMAGE_SIZES.SMALLER}
          />
        </div>
        <div>
          {canViewUserDetails ? (
            <>
              <Link className={styles.blackLink} to={userUrl(user?.id)}>
                {user?.displayName}
              </Link>
              <span className={classnames(styles.userDetails, styles.followers)}>
                {user?.activeFollowersCount} followers
              </span>
              {user?.mostRecentEmployer?.employer && (
                <div className={styles.userDetails}>
                  {user.mostRecentEmployer.currentEmployer ? '' : 'Formerly '}
                  {user.mostRecentEmployer.title ? `${user.mostRecentEmployer.title} at ` : 'at '}
                  <Link
                    className={classnames(styles.lightFont, styles.blackLink)}
                    to={employerProfileUrl(user.mostRecentEmployer.employer.id)}
                  >
                    {user.mostRecentEmployer.employer.name}
                  </Link>
                </div>
              )}
              {user?.featuredRank && (
                <span className={styles.userDetails}>{user.featuredRank.rankString}</span>
              )}
            </>
          ) : (
            <>
              <AnonymousUserDisplay anonUser={{ title: user?.mostRecentEmployer?.title }} />
              <span className={classnames(styles.userDetails, styles.followers)}>
                {user?.activeFollowersCount} followers
              </span>
              {user?.mostRecentEmployer?.employer && (
                <AnonEmployerAssociation
                  aumRange={user.mostRecentEmployer.employer?.aumRange || null}
                  fundType={user.mostRecentEmployer.employer?.fundType || null}
                  profileCity={user.profile?.city || null}
                />
              )}
            </>
          )}
        </div>
        {user && user.id !== currentUserId ? (
          <div>
            <FollowButton
              type={FollowableType.USER}
              id={user.id}
              buttonDisplay={canViewUserDetails && user.firstName ? user.firstName : ''}
              size={BUTTON_SIZES.TINY}
            />
            <MessageButton
              buttonOptions={{
                text: `Message ${user.firstName}`,
                icon: EnvelopeIcon,
                type: BUTTON_TYPES.SECONDARY,
                size: BUTTON_SIZES.TINY
              }}
              receiverEmployerName={user.mostRecentEmployer?.employer?.name || ''}
              isCurrentEmployer={Boolean(user.mostRecentEmployer?.currentEmployer)}
              receiverId={user.id}
              receiverName={user.displayName}
            />
          </div>
        ) : (
          <SocialMediaShareButtons.TwitterButton
            size="lg"
            shareText={untaggedBody || ''}
            shareUrl={`https://sumzero.com${postUrl(id)}`}
          />
        )}
      </div>
      <div className={styles.body}>
        <CommentBodyRenderer body={body} />
      </div>
      <div className={styles.tagSection}>
        {tagList &&
          tagList.map((tag, idx) => (
            <Link id={tag} className={styles.tag} key={idx} to={postsSearchUrl(`tag=${tag}`)}>
              {`#${tag}`}
            </Link>
          ))}
      </div>
      {youtubeId ? (
        <YouTubePlayer width={448} height={273} videoId={youtubeId} />
      ) : (
        <ArticlePreview
          domain={domain}
          ogDescription={ogDescription}
          ogUrl={ogUrl || ''}
          ogPreviewImageUrl={ogPreviewImageUrl}
          ogTitle={ogTitle}
        />
      )}
      <div className={styles.timestamp}>
        {moment(new Date(createdAt)).format('M/DD/YYYY [at] h:mm:ss a')}
      </div>
      {eventId ? (
        <div className={styles.actionWrap}>
          <Like
            currentUserHasLiked={currentUserHasLiked}
            id="post-like"
            likeCount={likeCount}
            onClick={(): Promise<FetchResult<LikeEventMutation>> =>
              likePost({ variables: { eventId } })
            }
          />
        </div>
      ) : null}
    </>
  );
};

export default Post;
